import type { CronJob, GatewaySessionRow, PresenceEntry } from "./types.ts";
import { formatAgo, formatDurationMs, formatMs } from "./format.ts";

export function formatPresenceSummary(entry: PresenceEntry): string {
  const host = entry.host ?? "unknown";
  const ip = entry.ip ? `(${entry.ip})` : "";
  const mode = entry.mode ?? "";
  const version = entry.version ?? "";
  return `${host} ${ip} ${mode} ${version}`.trim();
}

export function formatPresenceAge(entry: PresenceEntry): string {
  const ts = entry.ts ?? null;
  return ts ? formatAgo(ts) : "n/a";
}

// JAISIU-1509: derive a clear "online/offline/unknown" status from
// a presence entry. Gateways emit `mode: "gateway"` and `ts: Date.now()`;
// nodes emit `mode: "node"` and update `ts` on heartbeat. The TTL on the
// backend is 5 minutes (src/infra/system-presence.ts:TTL_MS); anything
// older than that is stale.
//
// We also accept "self" reason (the local gateway) as online even without
// a recent ts, since the gateway is always present on startup before any
// broadcast cycles run.
export type PresenceStatus = "online" | "offline" | "unknown";

export const PRESENCE_OFFLINE_AFTER_MS = 5 * 60 * 1000; // 5 minutes

export function derivePresenceStatus(
  entry: PresenceEntry,
  now: number = Date.now(),
): PresenceStatus {
  // Local gateway always considered online — it hasn't had time to
  // broadcast yet, but we know it exists.
  if (entry.reason === "self") {
    return "online";
  }
  if (entry.ts == null) {
    return "unknown";
  }
  return now - entry.ts <= PRESENCE_OFFLINE_AFTER_MS ? "online" : "offline";
}

export function presenceStatusLabel(status: PresenceStatus): string {
  switch (status) {
    case "online":
      return "online";
    case "offline":
      return "offline";
    case "unknown":
      return "unknown";
  }
}

// The "Last activity" field is what the user actually wants to see —
// `ts` is the last time the gateway/node sent a presence beacon. The
// existing "Last input" was confusing because `lastInputSeconds` is
// only meaningful for node-mode entries (a human typing on a paired
// device), not for gateways or backend services.
export function formatPresenceLastActivity(entry: PresenceEntry): string {
  if (entry.ts == null) {
    return "n/a";
  }
  return formatAgo(entry.ts);
}

export function formatNextRun(ms?: number | null) {
  if (!ms) {
    return "n/a";
  }
  return `${formatMs(ms)} (${formatAgo(ms)})`;
}

export function formatSessionTokens(row: GatewaySessionRow) {
  if (row.totalTokens == null) {
    return "n/a";
  }
  const total = row.totalTokens ?? 0;
  const ctx = row.contextTokens ?? 0;
  return ctx ? `${total} / ${ctx}` : String(total);
}

export function formatEventPayload(payload: unknown): string {
  if (payload == null) {
    return "";
  }
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    // oxlint-disable typescript/no-base-to-string
    return String(payload);
  }
}

export function formatCronState(job: CronJob) {
  const state = job.state ?? {};
  const next = state.nextRunAtMs ? formatMs(state.nextRunAtMs) : "n/a";
  const last = state.lastRunAtMs ? formatMs(state.lastRunAtMs) : "n/a";
  const status = state.lastStatus ?? "n/a";
  return `${status} · next ${next} · last ${last}`;
}

export function formatCronSchedule(job: CronJob) {
  const s = job.schedule;
  if (s.kind === "at") {
    const atMs = Date.parse(s.at);
    return Number.isFinite(atMs) ? `At ${formatMs(atMs)}` : `At ${s.at}`;
  }
  if (s.kind === "every") {
    return `Every ${formatDurationMs(s.everyMs)}`;
  }
  return `Cron ${s.expr}${s.tz ? ` (${s.tz})` : ""}`;
}

export function formatCronPayload(job: CronJob) {
  const p = job.payload;
  if (p.kind === "systemEvent") {
    return `System: ${p.text}`;
  }
  const base = `Agent: ${p.message}`;
  const delivery = job.delivery;
  if (delivery && delivery.mode !== "none") {
    const target =
      delivery.channel || delivery.to
        ? ` (${delivery.channel ?? "last"}${delivery.to ? ` -> ${delivery.to}` : ""})`
        : "";
    return `${base} · ${delivery.mode}${target}`;
  }
  return base;
}
